import {
  Control,
  FieldPath,
  FieldPathValue,
  FieldValues,
  useWatch,
} from 'react-hook-form';
import {useEffect, useRef} from 'react';

type UseFieldWatchCallback<
  TFieldValues extends FieldValues,
  TFieldName extends FieldPath<TFieldValues>
> = (
  value?: FieldPathValue<TFieldValues, TFieldName>,
  previous?: FieldPathValue<TFieldValues, TFieldName>
) => void;

/**
 * Begins listening for changes to the value of a field.
 * @param control The form's `control` object.
 * @param name The name of the field to watch.
 * @param callback Optional callback called when the value changes.
 * @returns Current, reactive value of the field.
 */
export const useFieldWatch = <
  TFieldValues extends FieldValues,
  TFieldName extends FieldPath<TFieldValues>
>(
  control: Control<TFieldValues>,
  name: TFieldName,
  callback?: UseFieldWatchCallback<TFieldValues, TFieldName>
): FieldPathValue<TFieldValues, TFieldName> | undefined => {
  const _previousValue = useRef<FieldPathValue<TFieldValues, TFieldName>>();
  const value = useWatch<TFieldValues>({name, control, exact: true});
  const _cb = useRef(callback);

  useEffect(() => {
    _cb.current = callback;
  }, [callback]);

  useEffect(() => {
    _cb.current?.(value, _previousValue.current);
  }, [value]);

  _previousValue.current = value;

  return value;
};